import styled, { keyframes } from 'styled-components'

import breakpoints from '../constants/breakpoints'

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`

const ProjectDetailsLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  .section-wrapper {
    padding-top: 3.5rem;

    @media (max-width: ${breakpoints.screenMD}) {
      padding-top: 1.5rem;
    }
  }
`

const Square = styled.div`
  background-color: ${({ background }) => background};
  color: ${({ text }) => text};
`

const SectionCover = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding-top: 30vh;
  padding-bottom: 4rem;
  min-height: 90vh;
  overflow: hidden;
  opacity: 0;
  animation: ${fadeIn} 1.2s ease forwards;

  @media (max-width: ${breakpoints.screenLG}) {
    padding-top: 17vh;
    min-height: 50vh;
  }

  @media (max-width: ${breakpoints.screenMD}) {
    padding-top: 20vh;
    min-height: 55vh;
  }

  @media (max-width: ${breakpoints.screenSM}) {
    padding-top: 45vh;
    min-height: 100vh;
  }

  .cover-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    img {
      height: 100%;
      width: 100%;
      object-fit: cover;
    }
  }

  .title {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 50%;
    z-index: 1;
    padding: 2.5rem 9rem 2rem;

    @media (max-width: ${breakpoints.screenL}) {
      width: 52%;
    }

    @media (max-width: ${breakpoints.screenLG}) {
      padding: 2.5rem 3rem 2rem;
    }

    @media (max-width: ${breakpoints.screenMD}) {
      padding: 1.5rem 1.5rem 1rem;
    }

    @media (max-width: ${breakpoints.screenSM}) {
      width: 90%;
    }
  }

  p {
    position: relative;
    padding-bottom: 0.5rem;
    margin-bottom: 2rem;

    @media (max-width: ${breakpoints.screenMD}) {
      margin-bottom: 1rem;
    }

    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: calc(100% + 3rem);
      height: 3px;
      background-color: ${({ theme }) => theme.textLight};
    }
  }

  .tags {
    margin-top: auto;
  }
`

const SectionIntro = styled.div`
  background-color: ${({ background }) => background};
  padding: 6rem 9rem;

  @media (max-width: ${breakpoints.screenLG}) {
    padding: 6rem 3rem;
  }

  @media (max-width: ${breakpoints.screenMD}) {
    padding: 4rem 1.5rem;
  }

  h2 {
    margin-bottom: 2rem;
  }
`

const SectionRichText = styled.div`
  padding: 4rem 9rem;

  @media (max-width: ${breakpoints.screenLG}) {
    padding: 4rem 3rem;
  }

  @media (max-width: ${breakpoints.screenMD}) {
    padding: 2rem 1.5rem;
  }

  &.reduce-top-padding {
    @media (min-width: ${breakpoints.screenMD}) {
      padding-top: 0.5rem;
    }
  }

  &.reduce-bottom-padding {
    @media (min-width: ${breakpoints.screenMD}) {
      padding-bottom: 0.5rem;
    }
  }

  .align-center {
    text-align: center;
  }

  h2 {
    font-size: 2.5rem;
    line-height: 1.2;
    font-family: ${({ theme }) => theme.fontPrimary};
    color: ${({ theme }) => theme.textPrimary};
    font-weight: 700;
    margin-bottom: 2rem;

    @media (max-width: ${breakpoints.screenMD}) {
      font-size: 1.9rem;
    }
  }

  p {
    font-size: 1.25rem;
    line-height: 1.6;
    font-family: ${({ theme }) => theme.fontPrimary};
    color: ${({ theme }) => theme.textPrimary};
    font-weight: 300;
    margin-bottom: 2rem;

    &:last-child {
      margin-bottom: 0;
    }

    @media (max-width: ${breakpoints.screenMD}) {
      font-size: 1.1rem;
    }
  }

  a {
    text-decoration: underline;
  }

  img,
  iframe {
    margin: 1rem 0 3rem;
  }

  iframe {
    min-width: 100%;
    min-height: 510px;
  }

  ul {
    list-style: none;
    font-size: 1.25rem;
    line-height: 1.6;
    font-family: ${({ theme }) => theme.fontPrimary};
    color: ${({ theme }) => theme.textPrimary};
    font-weight: 300;

    @media (max-width: ${breakpoints.screenMD}) {
      font-size: 1.1rem;
    }

    li {
      position: relative;
      padding-left: 3rem;
      padding-bottom: 1rem;

      &:last-child {
        padding-bottom: 0;
      }

      &:before {
        position: absolute;
        content: '';
        top: 10px;
        left: 0;
        width: 12px;
        height: 12px;
        border-radius: 100%;
        background-color: ${({ theme }) => theme.textPrimary};

        @media (max-width: ${breakpoints.screenSM}) {
          width: 10px;
          height: 10px;
        }
      }
    }
  }
`

const SectionCounterImage = styled.div`
  display: flex;
  flex-wrap: wrap;

  @media (max-width: ${breakpoints.screenSM}) {
    flex-direction: column;
  }

  &.reverse {
    flex-direction: row-reverse;

    @media (max-width: ${breakpoints.screenSM}) {
      flex-direction: column;
    }
  }

  .counter {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 50%;

    @media (max-width: ${breakpoints.screenSM}) {
      width: 100%;
      height: 450px;
    }
  }

  .image {
    width: 50%;

    @media (max-width: ${breakpoints.screenSM}) {
      width: 100%;
      height: 450px;
    }
  }

  img {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }

  h1 {
    margin-bottom: 1rem;
  }
`

const SectionCounterGrid = styled.div`
  display: flex;
  padding: 2rem 9rem;

  @media (max-width: ${breakpoints.screenLG}) {
    padding: 2rem 3rem;
  }

  @media (max-width: ${breakpoints.screenMD}) {
    padding: 2rem 1.5rem;
  }

  @media (max-width: ${breakpoints.screenSM}) {
    flex-direction: column;
  }

  .group {
    display: flex;
    width: 50%;

    @media (max-width: ${breakpoints.screenSM}) {
      width: 100%;

      &.reverse {
        flex-direction: row-reverse;
      }
    }
  }

  .counter {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    width: 50%;
    min-height: 350px;
    padding: 0 1rem;

    @media (max-width: ${breakpoints.screenL}) {
      min-height: 300px;
    }

    @media (max-width: ${breakpoints.screenLG}) {
      min-height: 250px;
    }

    @media (max-width: ${breakpoints.screenMD}) {
      min-height: 200px;
    }
  }

  h2 {
    margin-bottom: 1rem;
  }
`

const SectionFullImage = styled.div`
  padding: 4rem 9rem 0;

  @media (max-width: ${breakpoints.screenLG}) {
    padding: 4rem 3rem 0;
  }

  @media (max-width: ${breakpoints.screenMD}) {
    padding: 2rem 1.5rem 0;
  }
`

const SectionImagesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 2rem 0;

  @media (max-width: ${breakpoints.screenSM}) {
    flex-direction: column;
  }

  .image {
    width: 50%;

    @media (max-width: ${breakpoints.screenSM}) {
      width: 100%;
      height: 450px;
    }
  }

  img {
    height: 101%;
    width: 100%;
    object-fit: cover;
  }
`

const SectionFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 9rem 0;

  @media (max-width: ${breakpoints.screenLG}) {
    padding: 4rem 3rem 0;
  }

  @media (max-width: ${breakpoints.screenMD}) {
    padding: 2rem 1.5rem 0;
  }

  .mint-buttons {
    padding-top: 1rem;
  }

  .share-buttons {
    padding-top: 2rem;
  }

  .contact {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding-top: 4rem;

    img {
      max-width: 120px;
      margin-bottom: 3rem;
    }

    p {
      max-width: 30%;

      @media (max-width: ${breakpoints.screenL}) {
        max-width: 38%;
      }

      @media (max-width: ${breakpoints.screenLG}) {
        max-width: 45%;
      }

      @media (max-width: ${breakpoints.screenMD}) {
        max-width: 60%;
      }

      @media (max-width: ${breakpoints.screenSM}) {
        max-width: 90%;
      }
    }

    a {
      position: relative;
      color: ${({ theme }) => theme.linkHover};

      :after {
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 1px;
        background-color: ${({ theme }) => theme.linkHover};
      }
    }
  }
`

const SectionNavigator = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 8rem 9rem 6rem;

  @media (max-width: ${breakpoints.screenLG}) {
    padding: 8rem 3rem 6rem;
  }

  @media (max-width: ${breakpoints.screenMD}) {
    padding: 8rem 1.5rem 6rem;
  }

  @media (max-width: ${breakpoints.screenSM}) {
    padding: 4rem 1.5rem 3rem;

    p {
      font-size: 1.1rem;
    }
  }

  .disabled {
    opacity: 0.6;
  }
`

const StickyButtons = styled.div`
  position: sticky;
  top: 0;
  left: 10rem;
  width: fit-content;
  height: 0;
  z-index: 2;

  @media (max-width: ${breakpoints.screenLG}) {
    left: 1.5rem;
  }

  @media (max-width: ${breakpoints.screenMD}) {
    display: none;
  }
`

export {
  ProjectDetailsLayout,
  SectionCover,
  SectionIntro,
  SectionRichText,
  SectionCounterImage,
  SectionCounterGrid,
  SectionFullImage,
  SectionImagesGrid,
  SectionFooter,
  SectionNavigator,
  StickyButtons,
  Square,
}
